// AttendanceTable.js
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom"; // Add this import
import axiosInstance from "utils/axiosInstance";
import Image from "../../../components/appImage";
import Button from "../../../components/ui/button";
const BACKEND_URL = import.meta.env.VITE_API_URL;

const AttendanceTable = ({ limit = 5, showViewMore = true }) => { // Add props with defaults
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate(); // Add navigate hook

  // Fetch API Data
  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get("admin/dashboard/todays-attendance");
        const data = response?.data?.data?.records || [];
        console.log("Fetched attendance data:", data);
        setAttendanceRecords(data);
      } catch (err) {
        console.error("Error fetching attendance:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendance();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case "present":
        return "text-success bg-success/10 border-success/20";
      case "late":
        return "text-warning bg-warning/10 border-warning/20";
      case "absent":
        return "text-error bg-error/10 border-error/20";
      case "early_departure":
        return "text-warning bg-warning/10 border-warning/20";
      default:
        return "text-muted-foreground bg-muted border-border";
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date?.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleSelectRecord = (recordId) => {
    setSelectedRecords((prev) =>
      prev?.includes(recordId)
        ? prev?.filter((id) => id !== recordId)
        : [...prev, recordId]
    );
  };

  const handleSelectAll = () => {
    setSelectedRecords(
      selectedRecords?.length === attendanceRecords?.length
        ? []
        : attendanceRecords?.map((record) => record?.id)
    );
  };

  const handleViewMore = () => {
    navigate('/attendance-records');
  };

  const recordsToShow = limit ? attendanceRecords.slice(0, limit) : attendanceRecords;

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative bg-card border border-border rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
      
      {/* Decorative element */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary/5 to-accent/5 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>

      {/* Header */}
      <div className="relative z-10 p-6 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-primary/10 text-primary group-hover:scale-110 transition-transform duration-300">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground">Today's Attendance</h3>
              <p className="text-xs text-muted-foreground">{attendanceRecords.length} total records</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button 
              variant="outline" 
              iconName="Download" 
              iconPosition="left"
              className="hover:bg-primary/5 hover:text-primary hover:border-primary/30 transition-all duration-300"
            >
              Export
            </Button>
            <Button 
              variant="outline" 
              iconName="Filter" 
              iconPosition="left"
              className="hover:bg-accent/5 hover:text-accent hover:border-accent/30 transition-all duration-300"
            >
              Filter
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="relative z-10 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 backdrop-blur-sm">
            <tr>
              <th className="text-left p-4">
                <input
                  type="checkbox"
                  checked={
                    selectedRecords?.length === recordsToShow?.length &&
                    recordsToShow?.length > 0
                  }
                  onChange={handleSelectAll}
                  className="rounded border-border text-primary focus:ring-primary focus:ring-2 transition-all duration-200"
                />
              </th>
              <th className="text-left p-4 text-sm font-semibold text-foreground">
                Employee
              </th>
              <th className="text-left p-4 text-sm font-semibold text-foreground">
                Check In
              </th>
              <th className="text-left p-4 text-sm font-semibold text-foreground">
                Check Out
              </th>
              <th className="text-left p-4 text-sm font-semibold text-foreground">
                Status
              </th>
              <th className="text-left p-4 text-sm font-semibold text-foreground">
                Location
              </th>
              <th className="text-left p-4 text-sm font-semibold text-foreground">
                Hours
              </th>
              <th className="text-left p-4 text-sm font-semibold text-foreground">
                Notes
              </th>
            </tr>
          </thead>
          <tbody>
            {recordsToShow?.map((record, index) => (
              <tr
                key={record?.id}
                className="border-b border-border hover:bg-primary/5 transition-all duration-300 group/row animate-slide-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <td className="p-4">
                  <input
                    type="checkbox"
                    checked={selectedRecords?.includes(record?.id)}
                    onChange={() => handleSelectRecord(record?.id)}
                    className="rounded border-border text-primary focus:ring-primary focus:ring-2 transition-all duration-200"
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-border group-hover/row:ring-primary/50 transition-all duration-300">
                        <Image
                          src={`${BACKEND_URL}${record?.employee?.avatar}`}
                          alt={record?.employee?.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      {/* Online status indicator */}
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-success border-2 border-card rounded-full animate-pulse"></div>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground group-hover/row:text-primary transition-colors duration-300">
                        {record?.employee?.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {record?.employee?.employee_id} • {record?.employee?.department}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <div className="p-1.5 rounded bg-success/10 text-success">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-foreground">
                      {formatTime(record?.checkIn)}
                    </span>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    {record?.checkOut ? (
                      <>
                        <div className="p-1.5 rounded bg-error/10 text-error">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <span className="text-sm font-medium text-foreground">
                          {formatTime(record?.checkOut)}
                        </span>
                      </>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <span
                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-300 hover:scale-105 ${getStatusColor(
                      record?.status
                    )}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-current mr-2 animate-pulse"></span>
                    {record?.status?.replace("_", " ").toUpperCase()}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <div className="text-muted-foreground">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {record?.location}
                    </span>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <div className="p-1.5 rounded bg-primary/10 text-primary">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className="text-sm font-mono font-semibold text-foreground">
                      {record?.workingHours}
                    </span>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-sm text-muted-foreground">
                    {record?.notes || "-"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* View More Button */}
      {showViewMore && attendanceRecords.length > limit && (
        <div className="relative z-10 p-4 border-t border-border flex justify-center bg-card/50 backdrop-blur-sm">
          <Button 
            onClick={handleViewMore}
            variant="outline"
            className="group/btn hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 hover:scale-105"
          >
            <span>View All Attendance Records</span>
            <svg className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Button>
        </div>
      )}

      {/* Selected Records Actions */}
      {selectedRecords?.length > 0 && (
        <div className="relative z-10 p-4 border-t border-border bg-primary/5 backdrop-blur-sm animate-slide-up">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <div>
                <span className="text-sm font-semibold text-foreground">
                  {selectedRecords?.length} record(s) selected
                </span>
                <p className="text-xs text-muted-foreground">Choose an action below</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="sm"
                className="hover:bg-primary/10 hover:text-primary hover:border-primary/30 transition-all duration-300"
              >
                Bulk Edit
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                className="hover:bg-accent/10 hover:text-accent hover:border-accent/30 transition-all duration-300"
              >
                Export Selected
              </Button>
              <Button 
                variant="destructive" 
                size="sm"
                className="hover:scale-105 transition-all duration-300"
              >
                Delete Selected
              </Button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default AttendanceTable;